/*
 * The library takes every visible string as a prop (it never reads the app's
 * i18next). These builders give the shared ones — pagination, pickers, list
 * states, the discard guard — from the `common` namespace, so every screen says
 * them the same way. Screen-specific copy (empty states, column headers) stays
 * with the screen.
 */
import type { ReactNode } from "react";
import type { DiscardConfirmation, PickerMessages, TablePaginationLabels } from "@h2j-data-team/ui";
import i18n from "@/i18n";

const t = (key: string, options?: Record<string, unknown>) => i18n.t(`common:${key}`, options);

/** Labels of TablePagination and of DataTable's `pagination.labels`. */
export function paginationLabels(): TablePaginationLabels {
  return {
    navigation: t("list.pagination"),
    previous: t("pagination.previous"),
    next: t("pagination.next"),
    pageSize: t("pagination.rowsPerPage"),
    page: (page, count) =>
      count === undefined
        ? t("pagination.pageOnly", { page })
        : t("pagination.page", { page, count }),
    range: (start, end, total) => t("pagination.showing", { start, end, total }),
  };
}

/** Messages of Combobox and MultiSelect; pass `placeholder` for the field's own prompt. */
export function pickerMessages(overrides: Partial<PickerMessages> = {}): PickerMessages & {
  removeLabel: (label: string) => string;
  overflowLabel: (count: number) => string;
} {
  return {
    placeholder: t("selectPlaceholder"),
    searchPlaceholder: t("searchPlaceholder"),
    emptyLabel: t("noOptionsFound"),
    loadingLabel: t("list.loadingOptions"),
    selectedLabel: t("list.selected"),
    clearLabel: t("clearSelection"),
    removeLabel: (label) => t("list.remove", { label }),
    overflowLabel: (count) => t("list.more", { count }),
    ...overrides,
  };
}

type StateContent = {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
};

/**
 * DataTable's `messages`, given the screen's own empty and no-results copy.
 * `noResults` defaults to the shared "nothing for the current filters".
 */
export function tableMessages(content: { empty: StateContent; noResults?: StateContent }) {
  return {
    loading: t("list.loading"),
    updating: t("list.updating"),
    retry: t("tryAgain"),
    empty: content.empty,
    noResults: content.noResults ?? { title: t("list.noResults") },
  };
}

/** DataTable's `search`, with the screen's placeholder. */
export function tableSearch(placeholder: string) {
  return { label: t("list.search"), clearLabel: t("list.clearSearch"), placeholder };
}

/** The discard guard of an editable drawer or dialog (`dirty` + `discardConfirmation`). */
export function discardMessages(): DiscardConfirmation {
  return {
    title: t("discard.title"),
    description: t("discard.description"),
    keepEditingLabel: t("discard.keepEditing"),
    discardLabel: t("discard.discard"),
  };
}

/**
 * DataTable's `refreshing` from a query: only a page, search or filter change
 * still showing the previous rows (`placeholderData`), or a retry after an error.
 * Background refetches (polling, invalidation after a save) stay silent:
 * announcing "updating" on each would be noise.
 */
export function listRefreshing(query: {
  isPlaceholderData: boolean;
  isError: boolean;
  isFetching: boolean;
}) {
  return query.isPlaceholderData || (query.isError && query.isFetching);
}
